import React, { useState, useMemo } from 'react';

const RAW_DATA = [
  { id: 'EMP001', name: 'Alice Morgan', dept: 'Engineering', salary: 92000, level: 'Senior', rating: 4.8, joined: '2019-03-15', active: true },
  { id: 'EMP002', name: 'Bob Chen', dept: 'Marketing', salary: 67000, level: 'Junior', rating: 3.9, joined: '2021-07-20', active: true },
  { id: 'EMP003', name: 'Carol Davis', dept: 'Engineering', salary: 115000, level: 'Lead', rating: 4.9, joined: '2017-01-10', active: true },
  { id: 'EMP004', name: 'Dave Kim', dept: 'HR', salary: 58000, level: 'Junior', rating: 3.5, joined: '2022-05-01', active: false },
  { id: 'EMP005', name: 'Eve Wilson', dept: 'Finance', salary: 88000, level: 'Senior', rating: 4.2, joined: '2020-09-12', active: true },
  { id: 'EMP006', name: 'Frank Lee', dept: 'Engineering', salary: 78000, level: 'Mid', rating: 4.0, joined: '2021-02-28', active: true },
  { id: 'EMP007', name: 'Grace Park', dept: 'Marketing', salary: 72000, level: 'Mid', rating: 4.3, joined: '2020-11-05', active: true },
  { id: 'EMP008', name: 'Henry Brown', dept: 'Finance', salary: 105000, level: 'Lead', rating: 4.7, joined: '2018-06-30', active: true },
  { id: 'EMP009', name: 'Iris Patel', dept: 'HR', salary: 62000, level: 'Mid', rating: 3.8, joined: '2021-10-15', active: false },
  { id: 'EMP010', name: 'Jack Turner', dept: 'Engineering', salary: 98000, level: 'Senior', rating: 4.5, joined: '2019-08-22', active: true },
];

const DEPTS = ['All', 'Engineering', 'Marketing', 'Finance', 'HR'];
const LEVELS = ['All', 'Junior', 'Mid', 'Senior', 'Lead'];

export default function DataDashboard() {
  const [deptFilter, setDeptFilter] = useState('All');
  const [levelFilter, setLevelFilter] = useState('All');
  const [activeOnly, setActiveOnly] = useState(false);
  const [sortCol, setSortCol] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 5;

  const filtered = useMemo(() => {
    let d = RAW_DATA;
    if (deptFilter !== 'All') d = d.filter(r => r.dept === deptFilter);
    if (levelFilter !== 'All') d = d.filter(r => r.level === levelFilter);
    if (activeOnly) d = d.filter(r => r.active);
    return [...d].sort((a, b) => {
      let va = a[sortCol], vb = b[sortCol];
      if (typeof va === 'string') va = va.toLowerCase(), vb = vb.toLowerCase();
      return sortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
    });
  }, [deptFilter, levelFilter, activeOnly, sortCol, sortDir]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
    setPage(0);
  };

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const allSelected = paged.length > 0 && paged.every(r => selected.includes(r.id));
  const toggleAll = () => setSelected(allSelected ? selected.filter(id => !paged.find(r => r.id === id)) : [...new Set([...selected, ...paged.map(r => r.id)])]);

  const avgSalary = filtered.length ? Math.round(filtered.reduce((s, r) => s + r.salary, 0) / filtered.length) : 0;
  const avgRating = filtered.length ? (filtered.reduce((s, r) => s + r.rating, 0) / filtered.length).toFixed(1) : 0;
  const activeCount = filtered.filter(r => r.active).length;

  const SortIcon = ({ col }) => sortCol === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ↕';

  return (
    <div className="eval-section">
      <h2 className="eval-section-title">📊 Data Grid Dashboard <span className="eval-badge-scenario">Sorting / Filtering / Pagination / Selection</span></h2>
      <p className="eval-desc">Test: multi-column sorting, combined filters, row selection, pagination, computed aggregates.</p>

      <div className="dd-stats">
        <div className="dd-stat"><div className="dd-stat-val">{filtered.length}</div><div className="dd-stat-lbl">Employees</div></div>
        <div className="dd-stat"><div className="dd-stat-val">{activeCount}</div><div className="dd-stat-lbl">Active</div></div>
        <div className="dd-stat"><div className="dd-stat-val">${avgSalary.toLocaleString()}</div><div className="dd-stat-lbl">Avg Salary</div></div>
        <div className="dd-stat"><div className="dd-stat-val">{avgRating}</div><div className="dd-stat-lbl">Avg Rating</div></div>
        <div className="dd-stat dd-stat-sel"><div className="dd-stat-val">{selected.length}</div><div className="dd-stat-lbl">Selected</div></div>
      </div>

      <div className="dd-toolbar">
        <select value={deptFilter} onChange={e => { setDeptFilter(e.target.value); setPage(0); }}>
          {DEPTS.map(d => <option key={d}>{d}</option>)}
        </select>
        <select value={levelFilter} onChange={e => { setLevelFilter(e.target.value); setPage(0); }}>
          {LEVELS.map(l => <option key={l}>{l}</option>)}
        </select>
        <label className="dd-chk-lbl"><input type="checkbox" checked={activeOnly} onChange={e => { setActiveOnly(e.target.checked); setPage(0); }} /> Active only</label>
        {selected.length > 0 && <button className="eval-btn eval-btn-danger eval-btn-sm" onClick={() => setSelected([])}>Clear {selected.length} selected</button>}
      </div>

      <table className="eval-table">
        <thead>
          <tr>
            <th><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
            {[['name','Name'],['dept','Department'],['level','Level'],['salary','Salary'],['rating','Rating'],['joined','Joined'],['active','Status']].map(([col,lbl]) => (
              <th key={col} className="dd-sort-th" onClick={() => toggleSort(col)}>{lbl}<SortIcon col={col} /></th>
            ))}
          </tr>
        </thead>
        <tbody>
          {paged.map(r => (
            <tr key={r.id} className={selected.includes(r.id) ? 'dd-row-selected' : ''}>
              <td><input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggleSelect(r.id)} /></td>
              <td><strong>{r.name}</strong><br /><small style={{color:'#888'}}>{r.id}</small></td>
              <td>{r.dept}</td>
              <td><span className="dd-level-chip">{r.level}</span></td>
              <td>${r.salary.toLocaleString()}</td>
              <td>
                <span className="dd-rating">{'★'.repeat(Math.round(r.rating))}</span>
                <span style={{marginLeft:4,fontSize:12,color:'#666'}}>{r.rating}</span>
              </td>
              <td>{r.joined}</td>
              <td><span className={`sh-badge ${r.active ? 'sh-badge-yes' : 'sh-badge-no'}`}>{r.active ? 'Active' : 'Inactive'}</span></td>
            </tr>
          ))}
          {paged.length === 0 && <tr><td colSpan="8" style={{textAlign:'center',color:'#aaa',padding:'24px'}}>No records match.</td></tr>}
        </tbody>
      </table>

      <div className="dd-pagination">
        <span style={{fontSize:13,color:'#666'}}>
          Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}
        </span>
        <div className="dd-page-btns">
          <button className="eval-btn eval-btn-sm" onClick={() => setPage(0)} disabled={page === 0}>«</button>
          <button className="eval-btn eval-btn-sm" onClick={() => setPage(p => p - 1)} disabled={page === 0}>‹ Prev</button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button key={i} className={`eval-btn eval-btn-sm ${i === page ? 'eval-btn-primary' : ''}`} onClick={() => setPage(i)}>{i + 1}</button>
          ))}
          <button className="eval-btn eval-btn-sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>Next ›</button>
          <button className="eval-btn eval-btn-sm" onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}>»</button>
        </div>
      </div>
    </div>
  );
}
